import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Package, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/ProductCard';
import { productsAPI, suppliersAPI, orderCycleAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { fmtDateSAST, toSASTDateInput } from '@/lib/tz';

// Short day/month for the range text, e.g. "26 Jul".
function formatWindowDate(dateStr) {
  return fmtDateSAST(dateStr, { day: 'numeric', month: 'short' });
}

// Whole-day difference between the SAST calendar "today" and a target date.
function daysUntil(dateStr) {
  const target = toSASTDateInput(dateStr);
  const today = toSASTDateInput(new Date().toISOString());
  if (!target || !today) return null;
  const ms = Date.parse(`${target}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

// Friendly countdown copy for either an opening or a closing date.
function countdownText(dateStr, action) {
  const days = daysUntil(dateStr);
  if (days === null) return '';
  const verb = action === 'opens' ? 'Opens' : 'Closes';
  if (days <= 0) return action === 'opens' ? 'Opening now' : 'Closing today';
  if (days === 1) return `${verb} tomorrow`;
  return `${verb} in ${days} days`;
}

export default function HomePage() {
  const { user } = useAuth();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [featuredConfig, setFeaturedConfig] = useState({ visible: true, ids: [], banner_text: '', banner_visible: false });
  const [cycleMonths, setCycleMonths] = useState([]);

  useEffect(() => {
    suppliersAPI.getFeatured().then(r => {
      const config = r.data;
      setFeaturedConfig({
        visible: config.featured_section_visible,
        ids: config.featured_product_ids || [],
        banner_text: config.banner_text || '',
        banner_visible: config.banner_visible || false,
      });
      if (config.featured_section_visible && config.featured_product_ids?.length > 0) {
        productsAPI.list({}).then(pr => {
          const ids = new Set(config.featured_product_ids);
          setFeaturedProducts(pr.data.filter(p => ids.has(p.id)));
        }).catch(() => {});
      }
    }).catch(() => {});

    suppliersAPI.listWithWindows().then(r => setVendors(r.data)).catch(() => {});
    orderCycleAPI.get().then(r => setCycleMonths(r.data.months || [])).catch(() => {});
  }, []);

  // Split vendors into those taking orders now vs. those with an upcoming window.
  const openVendors = vendors.filter(v => (v.window_state ? v.window_state === 'open' : v.window_active));
  // Upcoming vendors sorted by the soonest order window first.
  const upcomingVendors = vendors
    .filter(v => v.window_state === 'upcoming')
    .sort((a, b) => (a.window_open_date || '').localeCompare(b.window_open_date || ''));

  const renderVendorCard = (vendor, i, upcoming) => (
    <Link
      key={vendor.id}
      to={`/shop?supplier=${vendor.id}`}
      data-testid={`vendor-card-${vendor.id}`}
      className="group animate-fade-up"
      style={{ animationDelay: `${i * 0.1}s` }}
    >
      <div className="relative rounded-2xl overflow-hidden bg-card shadow-md hover:shadow-xl transition-all duration-500 h-full flex flex-col">
        <div className="aspect-[4/3] flex items-center justify-center bg-white/5 p-4 sm:p-6 rounded-t-2xl">
          {vendor.image_url ? (
            <img
              src={vendor.image_url}
              alt={vendor.name}
              className="max-w-[80%] max-h-[80%] object-contain transition-transform duration-700 group-hover:scale-105 rounded-xl"
            />
          ) : (
            <Package className="h-12 w-12 sm:h-16 sm:w-16 text-teal/20" />
          )}
        </div>

        <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
          <span className={`text-[10px] sm:text-xs font-body font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-md shadow-sm ${
            upcoming ? 'bg-gold text-charcoal' : 'bg-green-400 text-green-900'
          }`}>
            {upcoming ? 'Opening Soon' : 'Now Taking Orders'}
          </span>
        </div>

        <div className="p-4 sm:p-5 border-t border-border flex-1 flex flex-col">
          <h3 className="font-heading text-lg sm:text-xl font-medium text-foreground">
            {vendor.name}
          </h3>
          {vendor.description && (
            <p
              className="font-body text-xs sm:text-sm text-foreground/60 mt-1 mb-4 line-clamp-3"
              data-testid={`vendor-desc-${vendor.id}`}
            >
              {vendor.description}
            </p>
          )}
          {/* Option A — Gold "Order Window" banner */}
          {vendor.window_open_date && vendor.window_close_date && (() => {
            // Upcoming cards highlight the OPEN date; live cards highlight the CLOSE date.
            const anchorDate = upcoming ? vendor.window_open_date : vendor.window_close_date;
            return (
              <div
                className="mt-auto flex items-stretch gap-3 rounded-xl border border-gold/40 bg-gradient-to-r from-gold/[0.18] to-gold/[0.05] p-3"
                data-testid={`vendor-window-${vendor.id}`}
              >
                <div className="flex w-11 flex-shrink-0 flex-col items-center justify-center rounded-lg bg-gold py-1.5 leading-none text-charcoal">
                  <span className="font-body text-[8px] font-extrabold uppercase tracking-[0.1em]">
                    {fmtDateSAST(anchorDate, { month: 'short' })}
                  </span>
                  <span className="font-body text-base font-extrabold">
                    {fmtDateSAST(anchorDate, { day: 'numeric' })}
                  </span>
                </div>
                <div className="flex flex-col justify-center min-w-0">
                  <span className="font-body text-[9px] font-extrabold uppercase tracking-[0.14em] text-gold">
                    {upcoming ? 'Order window opens' : 'Order window closes'}
                  </span>
                  <span className="font-body text-sm font-extrabold text-foreground truncate">
                    {formatWindowDate(vendor.window_open_date)} → {formatWindowDate(vendor.window_close_date)}
                  </span>
                  <span className="font-body text-[11px] text-foreground/55">
                    {countdownText(anchorDate, upcoming ? 'opens' : 'closes')}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </Link>
  );

  return (
    <div data-testid="home-page" className="bg-background">
      {/* Admin Banner */}
      {featuredConfig.banner_visible && featuredConfig.banner_text && (
        <div data-testid="admin-banner" className="bg-gold/10 border-b border-gold/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-center gap-3">
            <Megaphone className="h-4 w-4 sm:h-5 sm:w-5 text-gold flex-shrink-0" />
            <p className="font-body text-xs sm:text-sm text-gold font-medium">{featuredConfig.banner_text}</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[50vh] sm:min-h-[60vh] lg:min-h-[70vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-image.png"
            alt="Natural wellness products"
            className="w-full h-full object-cover"
            style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden' }}
          />
          <div className="absolute inset-0 bg-teal/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 w-full">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl sm:text-5xl lg:text-7xl font-normal text-white tracking-tight leading-[1.1] mb-6 sm:mb-8 animate-fade-up drop-shadow-lg">
              Wholesale Wellness,
              <span className="block italic mt-1 sm:mt-2">Simplified.</span>
            </h1>

            <Link to="/shop" className="animate-fade-up stagger-2 inline-block">
              <Button
                data-testid="shop-now-btn"
                variant="ctaPrimary"
                className="rounded-md px-8 sm:px-12 py-5 sm:py-7 text-sm sm:text-lg font-body font-medium transition-all duration-300 hover:-translate-y-1 shadow-xl hover:shadow-2xl backdrop-blur-sm"
              >
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Products Our Customers Love (Featured) */}
      {featuredConfig.visible && featuredProducts.length > 0 && (
        <section className="py-12 sm:py-20 bg-background" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-gold font-bold mb-2">
                Featured Collection
              </p>
              <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight">
                Products Our Customers Love
              </h2>
            </div>

            <div className="relative">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {featuredProducts.slice(0, 4).map((product, i) => (
                  <ProductCard key={product.id} product={product} index={i} />
                ))}
              </div>

              <button className="absolute -left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-card shadow-lg flex items-center justify-center text-teal hover:bg-teal hover:text-white transition-colors hidden lg:flex">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button className="absolute -right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-card shadow-lg flex items-center justify-center text-teal hover:bg-teal hover:text-white transition-colors hidden lg:flex">
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            <div className="text-center mt-8 sm:mt-12">
              <Link to="/shop">
                <Button variant="ctaSecondary" className="rounded-lg px-8 sm:px-10 py-5 sm:py-6 font-body font-semibold text-sm transition-all duration-300">
                  View All Products <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Shop by Vendor */}
      <section className="py-12 sm:py-20 bg-secondary/30" data-testid="vendors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold mb-2">
              Browse Vendors
            </p>
            <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight">
              Shop by Vendor
            </h2>
          </div>

          {/* Section 1 — Now Taking Orders */}
          <div className="mb-12 sm:mb-16" data-testid="vendors-open-section">
            <div className="flex items-center gap-3 mb-5 sm:mb-6">
              <span className="h-2.5 w-2.5 rounded-full bg-green-400 animate-pulse" />
              <h3 className="font-heading text-xl sm:text-2xl font-medium text-foreground">Now Taking Orders</h3>
              <span className="font-body text-xs text-foreground/50 bg-card px-2.5 py-1 rounded-full border border-border">
                {openVendors.length}
              </span>
            </div>
            {openVendors.length === 0 ? (
              <p className="font-body text-sm text-foreground/50" data-testid="vendors-open-empty">
                No vendors are taking orders right now — check the upcoming windows below.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {openVendors.map((vendor, i) => renderVendorCard(vendor, i, false))}
              </div>
            )}
          </div>

          {/* Section 2 — Opening Soon (next upcoming window) */}
          {upcomingVendors.length > 0 && (
            <div data-testid="vendors-upcoming-section">
              <div className="flex items-center gap-3 mb-5 sm:mb-6">
                <span className="h-2.5 w-2.5 rounded-full bg-gold" />
                <h3 className="font-heading text-xl sm:text-2xl font-medium text-foreground">Opening Soon</h3>
                <span className="font-body text-xs text-foreground/50 bg-card px-2.5 py-1 rounded-full border border-border">
                  {upcomingVendors.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {upcomingVendors.map((vendor, i) => renderVendorCard(vendor, i, true))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Order Cycle Calendar — rolling 6-month vendor plan (read-only) */}
      {cycleMonths.some(m => m.vendors && m.vendors.length > 0) && (
        <section className="py-12 sm:py-20 bg-background" data-testid="order-cycle-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-gold font-bold mb-2">
                Plan Ahead
              </p>
              <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight">
                Order Cycle Calendar
              </h2>
              <p className="font-body text-sm text-foreground/60 mt-3 max-w-2xl mx-auto">
                A look at which vendors are scheduled to take orders over the next six months.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {cycleMonths.map((m) => (
                <div
                  key={m.month_key}
                  data-testid={`cycle-month-${m.month_key}`}
                  className="rounded-2xl bg-card border border-border overflow-hidden shadow-sm"
                >
                  <div className="px-5 py-4 border-b-2 border-teal-dark bg-teal-dark text-center">
                    <h3 className="font-heading text-lg sm:text-xl font-bold tracking-[0.15em] text-gold-light uppercase">
                      {m.month_name}
                      {m.year !== new Date().getFullYear() && (
                        <span className="block font-body text-[11px] font-medium tracking-wider text-gold-light/60 mt-0.5">{m.year}</span>
                      )}
                    </h3>
                  </div>
                  <div>
                    {m.vendors.length === 0 ? (
                      <p className="font-body text-xs text-foreground/40 py-6 text-center">To be confirmed</p>
                    ) : (
                      m.vendors.map((v) => (
                        <div
                          key={v.id}
                          data-testid={`cycle-vendor-${m.month_key}-${v.id}`}
                          className="font-body text-sm sm:text-base text-foreground/80 text-center py-2.5 px-4 border-b border-border last:border-b-0 flex items-center justify-center gap-2.5"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" aria-hidden="true" />
                          {v.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA — only for signed-out visitors */}
      {!user && (
      <section className="py-16 sm:py-24 bg-background" data-testid="cta-join-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-4 sm:mb-5">
            Join the AVZ Family
          </h2>
          <p className="font-body text-sm sm:text-lg text-foreground/60 mb-8 sm:mb-10 max-w-xl mx-auto">
            Create your account to access wholesale pricing from our trusted suppliers. Place orders during open windows and collect at your convenience.
          </p>

          <Link to="/auth">
            <Button
              data-testid="cta-create-account-btn"
              variant="ctaPrimary"
              className="rounded-lg px-8 sm:px-12 py-5 sm:py-7 text-sm sm:text-lg font-body font-bold transition-all duration-300 hover:-translate-y-1 shadow-lg"
            >
              Create Your Account <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
      )}
    </div>
  );
}
